import React, { useCallback, useRef, useEffect, useContext } from "react";
import memoize from "memoize-one";
import { AppStateContext } from "../../containers/App";
import { InputCustomChangeEventHandler } from "../../types";
import debug from "../../utils/debug";
import { get, has, isArray } from "../../utils/helpers";
import { convertPlaceToFormModel } from "../../utils/googleMaps";
import InputText from "../InputText";
import { AnalyticsSessionContext } from "../../containers/CustomerPage";

export interface InputTextAutoCompleteGoogleMapsProps {
  id: string;
  name: string;
  value: any;
  onChange: InputCustomChangeEventHandler;
  country?: string;
  className: string;
}

/**
 * This is an externally managed memoized function to create an
 * autocomplete instance based off the input element. By pulling this
 * outside of the React dependencies, we can rely on getting the same
 * Google Maps instance when the input reference changes, either by
 * the input element being unmounted and removed, or otherwise.
 */
const getGoogleMapsRef = memoize(
  (inputRef: HTMLInputElement): google.maps.places.Autocomplete => {
    const googleMaps = new window.google.maps.places.Autocomplete(inputRef);

    /*debug(
      "[InputTextAutocompleteGoogleMaps] getGoogleMapsRef: created new google maps instance",
      { googleMaps }
    );*/

    return googleMaps;
  }
);

/**
 * Clears any elements from the DOM that the Google Maps Place Autocomplete
 * instance creates.
 */
function clearGoogleMapsElements() {
  Array.from(document.querySelectorAll<HTMLElement>(".pac-container")).forEach(
    pac => {
      debug("[InputTextAutocompleteGoogleMaps] clearGoogleMapsElements", {
        pac
      });

      pac.remove();
    }
  );
}

export default function InputTextAutocompleteGoogleMaps({
  onChange,
  country,
  ...props
}: InputTextAutoCompleteGoogleMapsProps) {
  const [appState] = useContext(AppStateContext);
  const config = appState.config;
  const isEnabled =
    // Disabled if Google Maps aren't loaded correctly
    !window.hasOwnProperty("google") ||
    // Disabled if app config has disabled Google Maps, otherwise defaults to true
    (has(config, "services.googleMaps.excludeCountries", isArray)
      ? !get(config, "services.googleMaps.excludeCountries").includes(country)
      : true);

  const analytics = useContext(AnalyticsSessionContext);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const googleMaps = useRef<google.maps.places.Autocomplete | null>(null);

  const handlePlaceSelect = useCallback(() => {
    if (googleMaps.current) {
      const place = googleMaps.current.getPlace();
      if (place) {
        const formModel = convertPlaceToFormModel(place);
        // The addressCountry and addressRegion aren't supposed to be included
        // the the data which is merged, hence why they are individually
        // destructured and not subsequently used.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        // update: By Celine request, replace also region
        //const { addressCountry, addressRegion, ...data } = formModel;
        const { addressCountry, ...data } = formModel;
        analytics.current.form.isAddressAutoCompleted = true;
        document.dispatchEvent(
          new CustomEvent("form.updateState", {
            detail: data
          })
        );

        // In order to force the form update in case the whole postal code is entered
        onChange("", props.id);
      }
    }
  }, [onChange]);

  useEffect(() => {
    // Create a new instance of Google Maps when the inputRef.current has
    // been set and the existing googleMaps.current hasn't been set.
    if (isEnabled && inputRef.current && !googleMaps.current) {
      googleMaps.current = getGoogleMapsRef(inputRef.current);

      googleMaps.current.setFields([
        "address_components",
        "formatted_address",
        "name",
        "types"
      ]);

      // Clear any previous listeners
      window.google.maps.event.clearInstanceListeners(googleMaps.current);

      // Add the handlePlaceSelect as a listener
      googleMaps.current.addListener("place_changed", handlePlaceSelect);

      /*debug(
        "[InputTextAutocompleteGoogleMaps] assigned behaviours to google maps instance",
        {
          googleMaps: googleMaps.current
        }
      );*/
    }

    // If not enabled, make sure we clean up the Google Maps instance
    if (!isEnabled && googleMaps.current) {
      debug(
        "[InputTextAutocompleteGoogleMaps] not enabled, so removing elements",
        {
          googleMaps: googleMaps.current
        }
      );

      window.google.maps.event.clearInstanceListeners(googleMaps.current);
      googleMaps.current = null;
      clearGoogleMapsElements();
    }
  }, [isEnabled, handlePlaceSelect]);

  // Update the googleMaps restrictions when the country changes
  useEffect(() => {
    if (isEnabled && googleMaps.current && country) {
      googleMaps.current.setComponentRestrictions({
        country
      });
    }
  }, [isEnabled, country]);

  // Remove the Google Maps instance and elements listeners when unmounted
  useEffect(
    () => () => {
      if (googleMaps.current) {
        debug(
          "[InputTextAutocompleteGoogleMaps] unmounting, so removing elements",
          {
            googleMaps: googleMaps.current
          }
        );

        window.google.maps.event.clearInstanceListeners(googleMaps.current);
        googleMaps.current = null;
        clearGoogleMapsElements();
      }
    },
    []
  );

  return (
    <InputText
      {...props}
      onChange={onChange}
      ref={inputRef}
      placeholder=""
      type="text"
    />
  );
}
